import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {useSnackbar} from "./SnackbarContext";
import {parseError} from "../errors/http.service";
import {getCookie} from "../utils/cookies";
import {accessTokenKey} from "../core/AuthInterceptor";
import {usersService} from "../services/users.service";
import {CurrentUser} from "../models/users.models";

interface UserContextType {
    user: CurrentUser | null;
    isLoadingUser: boolean;
    fetchUserData: () => Promise<void>;
    refreshConversations: number;
    triggerConversationRefresh: () => void;
    resetUserData: () => void;
}

// --- Context ---

const UserContext = createContext<UserContextType | undefined>(undefined);

// --- Provider ---

// TODO: verify if functions should be in useCallback
export const UserProvider = ({children}: {children: ReactNode}) => {
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [refreshConversations, setRefreshConversations] = useState(0)

    const {showSnackbar} = useSnackbar();

    const fetchInitiatedRef = React.useRef(false);

    const fetchUserData = useCallback(async () => {
        const token = getCookie(accessTokenKey);
        if (!token) {
            console.error("No token provided");
            setIsLoadingUser(false);
            return;
        }

        try {
            setIsLoadingUser(true);
            const data = await usersService.fetchLoggedInUser();
            setUser(data);
        } catch (error) {
            showSnackbar(parseError(error), "error");
            setUser(null);
        } finally {
            setIsLoadingUser(false);
        }
    }, []);

    const resetUserData = () => {
        setUser(null);
        fetchInitiatedRef.current = false;
    };

    const triggerConversationRefresh = () => {
        setRefreshConversations(prev => prev + 1);
    }

    useEffect(() => {
        if (!user && !fetchInitiatedRef.current) {
            fetchInitiatedRef.current = true;
            void fetchUserData();
        }
    }, []);

    return (
        <UserContext.Provider
            value={{user, isLoadingUser, fetchUserData, refreshConversations, triggerConversationRefresh, resetUserData}}>
            {children}
        </UserContext.Provider>
    );
};

// --- Hook ---

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
